import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
} from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import {
  ICourseStudentForAdmin,
  TCreateCourseStudent,
  TUpdateCourseStudent,
  createCourseStudent,
  updateCourseStudent,
} from '../../../../../../../../store/V2/CourseStudentsSlice';
import { getCoursesState } from '../../../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../../../hooks';
import { TPTextField } from '../../../../../../../../components/Inputs';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../../../components/Inputs/UserListDropdown';

export type TState = {
  student: TUserSelectOption | null;
  startDate: Moment | null;
  endDate: Moment | null;
};

interface AddCourseStudentModalModalProps {
  open: boolean;
  onClose: () => void;
  mode?: 'edit';
  courseStudent?: ICourseStudentForAdmin;
}

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
};

const initialState: TState = {
  student: null,
  startDate: null,
  endDate: null,
};

const AddCourseStudentModal: React.FC<AddCourseStudentModalModalProps> = ({
  open,
  onClose,
  mode,
  courseStudent,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);

  const courseData = coursesState.adminCourse;
  const isSubmitDisabled = !state.student || !state.endDate;

  useEffect(() => {
    if (mode === 'edit' && courseStudent) {
      setState((prevState) => {
        const newState: TState = { ...prevState };

        if (courseStudent) {
          if (courseStudent.student && courseStudent.student._id) {
            const _student = courseStudent.student;

            let _name = _student.firstname;
            let _roleName = '';

            if (_student.lastname) {
              _name += ` ${_student.lastname}`;
            }

            if (_student.role && _student.role.name) {
              _roleName = _student.role.name;
              _name += ` (${_roleName})`;
            }

            newState.student = {
              label: _name,
              value: _student._id,
              roleName: _roleName,
            };
          }

          if (courseStudent.startDate) {
            newState.startDate = moment(courseStudent.startDate);
          }

          if (courseStudent.endDate) {
            newState.endDate = moment(courseStudent.endDate);
          }
        }

        return newState;
      });
    }
  }, []);

  const handleStudentChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TUserSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      student: value,
    }));
  };

  const handleStartDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        startDate: value,
      }));
    }
  };

  const handleEndDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        endDate: value,
      }));
    }
  };

  const handleSubmit = async () => {
    if (courseData && state.student) {
      if (mode === 'edit') {
        if (courseStudent) {
          setLoading(true);

          const apiData: TUpdateCourseStudent = {
            id: courseStudent._id,
          };

          if (state.startDate) {
            apiData.startDate = state.startDate.clone().toISOString();
          }

          if (state.endDate) {
            apiData.endDate = state.endDate.clone().toISOString();
          }

          if (Object.keys(apiData).length > 1) {
            await dispatch(updateCourseStudent(apiData));
          }

          setLoading(false);
        }
      } else {
        setLoading(true);

        const apiData: TCreateCourseStudent = {
          course: courseData._id,
          student: state.student.value,
        };

        if (state.startDate) {
          apiData.startDate = state.startDate.clone().toISOString();
        }

        if (state.endDate) {
          apiData.endDate = state.endDate.clone().toISOString();
        }

        await dispatch(createCourseStudent(apiData));

        setLoading(false);
      }

      onClose();
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>
        {mode === 'edit' ? 'Edit Student' : 'Add New Student'}
      </DialogTitle>
      <DialogContent sx={{ pt: `${theme.spacing(2)} !important` }}>
        <Box role="presentation">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Student *
                </Typography>
              </div>
              <FormControl fullWidth>
                <UserListDropdown
                  disabled={mode === 'edit'}
                  selectedItem={state.student}
                  handleChange={handleStudentChange}
                />
              </FormControl>
            </Grid>
            {mode !== 'edit' ? (
              <Grid item xs={12}>
                <div style={{ marginBottom: '15px' }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Start Date
                  </Typography>
                </div>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      disableCloseOnSelect={false}
                      value={state.startDate}
                      onChange={handleStartDateChange}
                      inputFormat="YYYY MMM DD"
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          placeholder="Start Date"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <InputAdornment position="end">
                                <DateRangeIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  End Date *
                </Typography>
              </div>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDatePicker
                    disableCloseOnSelect={false}
                    value={state.endDate}
                    onChange={handleEndDateChange}
                    inputFormat="YYYY MMM DD"
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="End Date"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <DateRangeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <LoadingButton
          size="small"
          variant="contained"
          color="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          loading={isLoading}
        >
          {mode === 'edit' ? 'Save' : 'Add Student'}
        </LoadingButton>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCourseStudentModal;
